/**
 * Pipeline Monitor Component
 * Combines Active Pipelines and Pipeline History in a tabbed interface
 */

import React, { useState } from 'react';
import { Box, Tabs, Tab, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import ActivePipelines from './ActivePipelines.tsx';
import PipelineHistory from './PipelineHistory.tsx';

interface PipelineMonitorProps {
  onWatchPipeline?: (pipelineId: string, replayHistory: boolean) => void;
}

const PipelineMonitor: React.FC<PipelineMonitorProps> = ({ onWatchPipeline }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [watchDialogOpen, setWatchDialogOpen] = useState(false);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleWatchPipelineClick = (pipelineId: string) => {
    setSelectedPipelineId(pipelineId);
    setWatchDialogOpen(true);
  };

  const handleWatchPipeline = (replayHistory: boolean) => {
    if (selectedPipelineId && onWatchPipeline) {
      onWatchPipeline(selectedPipelineId, replayHistory);
      setWatchDialogOpen(false);
      setSelectedPipelineId(null);
    }
  };

  const handleCloseDialog = () => {
    setWatchDialogOpen(false);
    setSelectedPipelineId(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Active Pipelines" />
          <Tab label="Pipeline History" />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {currentTab === 0 && (
          <ActivePipelines onWatchPipeline={handleWatchPipelineClick} />
        )}
        {currentTab === 1 && (
          <PipelineHistory />
        )}
      </Box>

      {/* Watch Pipeline Dialog */}
      <Dialog open={watchDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Watch Pipeline</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            How would you like to watch this pipeline?
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={() => handleWatchPipeline(false)}
            variant="outlined"
            color="primary"
          >
            Current State
          </Button>
          <Button
            onClick={() => handleWatchPipeline(true)}
            variant="contained"
            color="primary"
          >
            Replay from Beginning
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PipelineMonitor;
